import './tripDetails.scss';

import { Card } from '@transferz/fe-component-library/src/components/card/Card';
import { ShipIcon } from '@transferz/fe-component-library/src/components/icons';
import Calendar from '@transferz/fe-component-library/src/components/icons/CalendarIcon';
import Clock from '@transferz/fe-component-library/src/components/icons/ClockIcon';
import { PlaneIcon } from '@transferz/fe-component-library/src/components/icons/PlaneIcon';
import Timer from '@transferz/fe-component-library/src/components/icons/TimerIcon';
import { TrainIcon } from '@transferz/fe-component-library/src/components/icons/TrainIcon';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { HubType, JourneyStatusType } from '@transferz/fe-models';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocalize } from 'localize-react';
import React, { useState } from 'react';

import FlightTrackingLabel from '../../../../../src/routes/booking/components/flightTrackingLabel/FlightTrackingLabel';
import { GeoDirection } from '../../../../components/geoDirection/GeoDirection';
import { useRenderLocalizedTemplate } from '../../../../hooks/useRenderLocalizedTemplate';
import { formatDate, formatTime, getTimeZoneShortNameAndOffset } from '../../../../utils/utils';
import { LocationIcon } from '../locationIcon/LocationIcon';

export const TripDetails: React.FunctionComponent<any> = ({ journey }) => {
  const flags = useFlags();
  const { translate } = useLocalize();
  const { renderLocalizedTemplate } = useRenderLocalizedTemplate();
  const journeyIsNotPending = journey?.status !== JourneyStatusType.Pending;
  const [isCollapsed, setIsCollapsed] = useState(journeyIsNotPending);
  const [distanceDuration, setDistanceDuration] = useState({}) as any;
  const { journeyExecutionInfo } = journey;
  const hubType = journey?.hub?.hubType;

  const onDistanceAndDurationResolved = (distance: any, duration: any) => {
    distanceDuration.distance = distance;
    distanceDuration.duration = duration;
    setDistanceDuration({ ...distanceDuration });
  };
  const isFAQPlanned = journey.status === JourneyStatusType.Planned
    || journey.status === JourneyStatusType.Confirmed
    || journey.status === JourneyStatusType.DriverArrived
    || journey.status === JourneyStatusType.DriverUnderway;
  const onCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className={`trip-details card-wrap ${isFAQPlanned ? 'faq-planned' : ''}`}>
      <Card title={translate('booking_tripDetails')} collapsed={isCollapsed} onCollapse={onCollapse}>
        <div>
          {journeyIsNotPending && window.innerWidth < 768 && !isCollapsed && (
            <GeoDirection
                  key={journey?.inbound ? journey?.destinationLocation?.address?.formattedAddress : journey?.originLocation?.address?.formattedAddress}
                  origin={journey?.originLocation?.position}
                  destination={journey?.destinationLocation?.position}
                  position={journey?.originLocation?.position}
                  center={journey?.originLocation?.position}
                  onDistanceAndDurationResolved={(distance: any, duration: any) => onDistanceAndDurationResolved(distance, duration)}
                  name="geographicLocation"
            />
          )}
          {journeyIsNotPending && window.innerWidth > 768 && isCollapsed && (
            <GeoDirection
                  key={journey?.inbound ? journey?.destinationLocation?.address?.formattedAddress : journey?.originLocation?.address?.formattedAddress}
                  origin={journey?.originLocation?.position}
                  destination={journey?.destinationLocation?.position}
                  position={journey?.originLocation?.position}
                  center={journey?.originLocation?.position}
                  onDistanceAndDurationResolved={(distance: any, duration: any) => onDistanceAndDurationResolved(distance, duration)}
                  name="geographicLocation"
            />
          )}
          {!journeyIsNotPending && (
            <GeoDirection
                  key={journey?.inbound ? journey?.destinationLocation?.address?.formattedAddress : journey?.originLocation?.address?.formattedAddress}
                  origin={journey?.originLocation?.position}
                  destination={journey?.destinationLocation?.position}
                  position={journey?.originLocation?.position}
                  center={journey?.originLocation?.position}
                  onDistanceAndDurationResolved={(distance: any, duration: any) => onDistanceAndDurationResolved(distance, duration)}
                  name="geographicLocation"
            />
          )}
          <div className="pb-32 trip-info-item info-first">
            <LocationIcon location={journey?.originLocation} inbound={journey?.inbound} />
            <Typography variant="body1">
              {journey?.originLocation?.hub ? journey?.originLocation?.hubData?.autoCompleteName : journey?.originLocation?.address?.originalAddress
                ? journey?.originLocation?.address?.originalAddress : journey?.originLocation?.address?.formattedAddress}
            </Typography>
          </div>
          <div className="trip-info-item info-second">
            <LocationIcon location={journey?.destinationLocation} inbound={journey?.inbound} />
            <Typography variant="body1">
              {journey?.destinationLocation?.hub ? journey?.destinationLocation?.hubData?.autoCompleteName : journey?.destinationLocation?.address?.originalAddress
                ? journey?.destinationLocation?.address?.originalAddress : journey?.destinationLocation?.address?.formattedAddress}
            </Typography>
          </div>
        </div>
        <div className="trip-info-item trip-info-distance">
          <div className="date trip-info-distance-item">
            <Calendar className="icon-calendar" width="22" height="22" />
            <Typography variant="body1">{formatDate(journeyExecutionInfo?.pickupDate, 'DD-MM-YYYY')}</Typography>
          </div>
          <div className="time trip-info-distance-item">
            <Clock className="icon-clock" direction="right" width="20" height="20" />
            <div>
              <div className="time-container">
                <Typography variant="body1">{formatTime(journeyExecutionInfo?.pickupDate, 'HH:mm')}</Typography>
                {flags?.transferTimelineEnabled && journey?.journeyExecutionInfo?.pickupTimeAdjustmentMethod === 'FLIGHT_TRACKING' && <FlightTrackingLabel />}
              </div>
              <Typography variant="body2" color="asphalt5">{`${translate('booking_localTime')} ${getTimeZoneShortNameAndOffset(journey?.journeyExecutionInfo?.pickupTimeZone)}`}</Typography>
            </div>
          </div>
          <div className="duration trip-info-distance-item">
            <Timer className="icon-timer" direction="right" width="25" height="25" />
            {distanceDuration?.duration && <Typography variant="body1">{distanceDuration?.duration} ({distanceDuration?.distance})</Typography>}
          </div>
        </div>

        <div className="trip-info-item trip-info-number">
          {hubType === HubType.Airport && journey?.travellerInfo?.flightNumber ? (
            <div className="trip-info-number-item">
              <PlaneIcon className="plain-icon" />
              <Typography variant="body1"> {journey?.travellerInfo?.flightNumber}</Typography>
            </div>
          ) : hubType === HubType.TrainStation && journey?.travellerInfo?.trainNumber ? (
            <div className="trip-info-number-item">
              <TrainIcon className="train-icon" />
              <Typography variant="body1"> {journey?.travellerInfo?.trainNumber}</Typography>
            </div>
          ) : hubType === HubType.Seaport && journey?.travellerInfo?.shipName ? (
            <div className="trip-info-number-item">
              <ShipIcon className="ship-icon" />
              <Typography variant="body1"> {journey?.travellerInfo?.shipName}</Typography>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
};
